// The Term Sheet Gaps sheet of Sculpt's term sheet reconciliation (sculpt-api PLAN-pf-skills.md §10) — pure layout,
// no Office.js. One row per term, the gaps first (the order of the card); a term with several candidates in the model
// gets them as sub-rows the executor groups under it; a term not in the model gets what the model holds in its place
// (the tool's `instead`) in the Model and Location columns, the same way. Every place is an internal `HYPERLINK`.
// Fed by the same `TermsView` the pane's terms card draws, so the sheet is the card, not the model's prose.
import { colToLetter } from './a1';
import { cellLink, textCell } from './auditSheet';
import type { FindingsSheetLayout } from './findingsSheet';
import type { TermInsteadView, TermStatus, TermsView } from '../../types/agent';

export const TERMS_SHEET_NAME = 'Term Sheet Gaps';
export const TERMS_COLUMNS = ['#', 'Status', 'Term', 'Document', 'Model', 'Location', 'Gap', 'Note'] as const;
export const TERMS_HEADER_ROW = 4;
export const TERMS_FIRST_ROW = 5;
export const TERMS_HEADER_FILL = '#D9E1F2';
export const TERMS_STATUS_FILLS: Record<TermStatus, string> = {
  gap: '#F8CBAD', ambiguous: '#FFE699', 'to judge': '#FFE699', 'not in the model': '#DDEBF7', matches: '#E2EFDA', located: '#FFFFFF',
};
const STATUS_TEXT: Record<TermStatus, string> = {
  gap: 'Gap', ambiguous: 'Ambiguous', 'to judge': 'To judge', 'not in the model': 'Not in the model', matches: 'Matches', located: 'Located',
};
/** Free-text columns keep a width (character units) and wrap; the others are autofitted. */
export const TERMS_WRAP_WIDTHS: Record<string, number> = { Document: 40, Note: 45, Gap: 30 };
/** Column A holds the numbers but also the title above the table: a fixed width (see `findingsSheet.ts`). */
export const TERMS_ID_COLUMN_WIDTH = 5;

type Cell = string | number | null;

/** What the model holds in the place of a term it lacks, as the Model column writes it. */
function insteadText(c: TermInsteadView): string {
  return `In its place: ${c.label} = ${c.value ?? '—'}`;
}

export function layoutTermsSheet(view: TermsView, workbookName: string): FindingsSheetLayout {
  const cols = TERMS_COLUMNS.length;
  const blank = (): Cell[] => Array.from({ length: cols }, () => '');
  const title = blank();
  title[0] = textCell(`Term sheet against the model — ${workbookName}`);
  const count = (s: TermStatus) => view.rows.filter((r) => r.status === s).length;
  const subtitle = blank();
  subtitle[0] = textCell([
    `${count('gap')} gaps, ${count('ambiguous')} ambiguous, ${count('to judge')} to judge, ${count('not in the model')} not in the model, ${count('matches')} matching`,
    'a gap is the model minus the document',
    view.not_stated.length ? `not stated by the document: ${view.not_stated.join(', ')}` : '',
  ].filter(Boolean).join(' · '));
  const rows: Cell[][] = [];
  const fills: FindingsSheetLayout['fills'] = [];
  const groups: FindingsSheetLayout['groups'] = [];
  view.rows.forEach((r, i) => {
    const row = TERMS_FIRST_ROW + rows.length;
    const first = r.model[0];
    const instead = r.model.length ? [] : r.instead ?? [];
    const model = first ? `${first.value ?? ''}${first.computed ? ' (computed)' : ''}` : instead[0] ? insteadText(instead[0]) : '';
    const place = first ?? instead[0];
    rows.push([
      i + 1, textCell(STATUS_TEXT[r.status]), textCell(r.qualifier ? `${r.term} (${r.qualifier})` : r.term),
      textCell(r.document ?? ''), textCell(model), place ? cellLink(place.sheet, place.address) : '', textCell(r.gap ?? ''),
      textCell(r.note ?? ''),
    ]);
    fills.push({ address: `B${row}`, color: TERMS_STATUS_FILLS[r.status] });
    if (r.model.length > 1) {
      for (const c of r.model.slice(1)) {
        rows.push(['', '', textCell(`  ${c.label}`), '', textCell(c.value ?? ''), cellLink(c.sheet, c.address), '', '']);
      }
      groups.push({ from: row + 1, to: row + r.model.length - 1 });
    }
    if (instead.length > 1) {
      for (const c of instead.slice(1)) {
        rows.push(['', '', '', '', textCell(insteadText(c)), cellLink(c.sheet, c.address), '', '']);
      }
      groups.push({ from: row + 1, to: row + instead.length - 1 });
    }
  });
  const grid = [title, subtitle, blank(), TERMS_COLUMNS.map((c) => c as string), ...rows];
  const last = grid.length;
  const lastCol = colToLetter(cols);
  return {
    grid,
    address: `A1:${lastCol}${last}`,
    headerAddress: `A${TERMS_HEADER_ROW}:${lastCol}${TERMS_HEADER_ROW}`,
    tableAddress: rows.length ? `A${TERMS_HEADER_ROW}:${lastCol}${last}` : null,
    fills,
    groups,
    autofitAddress: `B${TERMS_HEADER_ROW}:${lastCol}${last}`,
    widths: [
      { column: 'A', width: TERMS_ID_COLUMN_WIDTH, wrap: false },
      ...TERMS_COLUMNS.map((c, i) => ({ column: colToLetter(i + 1), width: TERMS_WRAP_WIDTHS[c], wrap: true }))
        .filter((w): w is { column: string; width: number; wrap: boolean } => w.width !== undefined),
    ],
    freezeRows: TERMS_HEADER_ROW,
    findingRows: view.rows.length,
  };
}
